import { useEffect, useRef } from 'react';
import {
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { NormalizedLoad } from 'types/Load';

import { PlsFormValues } from '../types';
import {
  plsChargeAccessorialCode,
  plsSpecsAccessorialCharge,
  plsSpecsAccessorialRates,
} from '../utils/plsAccessorialCharges';

interface UsePlsSpecsAccessorialChargesParams {
  formMethods: UseFormReturn<PlsFormValues>;
  // Builder only. Load View reads every charge back from PLS's pricing lines.
  enabled: boolean;
  append: UseFieldArrayAppend<NormalizedLoad, 'rateData.customerLineItems'>;
  remove: UseFieldArrayRemove;
}

// Create bills every accessorial picked in Specs, at the rate card's charge for it, else PLS's
// default rate. The rate card lists each one as a charge, the way the shared rate card lists every
// charge for other TMSs, so the rep sees and prices what PLS will bill:
//   - picking an accessorial in Specs adds its charge to the card;
//   - unpicking it removes the charge it added;
//   - removing a charge on the card unpicks its accessorial in Specs.
// A charge added only on the card stays card-only; create adds it as its own line.
// Returns the card's remove handler.
export function usePlsSpecsAccessorialCharges({
  formMethods,
  enabled,
  append,
  remove,
}: UsePlsSpecsAccessorialChargesParams): (index: number) => void {
  const { control, getValues, setValue } = formMethods;
  const [specsAccessorials, charges] = useWatch({
    control,
    name: ['accessorials', 'rateData.customerLineItems'],
  });
  // Codes of the charges that stand for a Specs pick, so unpicking in Specs removes only those.
  const addedCodesRef = useRef(new Set<string>());
  // Charges the rep put on the card before picking the same accessorial in Specs. They keep their
  // price and stay when the pick is removed.
  const cardOnlyCodesRef = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const specsRates = plsSpecsAccessorialRates(specsAccessorials);
    // Read the card from the form, not the watched value: an append from an earlier run is there
    // before the watch catches up, so a charge isn't added twice.
    const cardCodes = (getValues('rateData.customerLineItems') ?? []).map(
      plsChargeAccessorialCode
    );

    cardCodes.forEach((code) => {
      if (code && !specsRates.has(code) && !addedCodesRef.current.has(code)) {
        cardOnlyCodesRef.current.add(code);
      }
    });
    cardOnlyCodesRef.current.forEach((code) => {
      if (!cardCodes.includes(code)) {
        cardOnlyCodesRef.current.delete(code);
      }
    });

    const unpicked = cardCodes.flatMap((code, index) =>
      addedCodesRef.current.has(code) && !specsRates.has(code) ? [index] : []
    );
    unpicked.forEach((index) => addedCodesRef.current.delete(cardCodes[index]));
    if (unpicked.length > 0) {
      remove(unpicked);
    }

    specsRates.forEach((rate, code) => {
      if (cardCodes.includes(code)) {
        // A charge already on the card for a Specs pick (a restored draft or a remount) belongs to
        // that pick, so unpicking it removes the charge. A charge the rep added first stays theirs.
        if (!cardOnlyCodesRef.current.has(code)) {
          addedCodesRef.current.add(code);
        }
        return;
      }
      addedCodesRef.current.add(code);
      append(plsSpecsAccessorialCharge(code, rate), { shouldFocus: false });
    });
  }, [enabled, specsAccessorials, charges, getValues, append, remove]);

  return (index: number) => {
    const code = plsChargeAccessorialCode(
      getValues(`rateData.customerLineItems.${index}`)
    );
    remove(index);
    addedCodesRef.current.delete(code);
    if (!enabled || !code) {
      return;
    }
    const picked = getValues('accessorials') ?? [];
    const kept = picked.filter(
      (item) => !plsSpecsAccessorialRates([item]).has(code)
    );
    if (kept.length !== picked.length) {
      setValue('accessorials', kept, { shouldDirty: true });
    }
  };
}
